import React from 'react'

import type { IconNames } from '@gousto-internal/citrus-react'
import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  DisplayPosition,
  FontFamily,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import { boxSummaryVisibilityChange } from 'actions/boxSummary'
import { registerBoxSummaryTreatmentFocusTarget } from 'routes/Menu/components/BoxSummary/treatmentFocus'
import { trackEditBoxSizeOrDate } from 'routes/Menu/components/CollectionsNav/BoxSummaryPicker/BoxSummaryPicker'
import { boxTypes } from 'routes/Menu/components/CollectionsNav/BoxSummaryPicker/__config__/boxTypes'
import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { getBasketDate, getBasketRecipesCount, getNumPortions } from 'selectors/basket'

const trackingSection = 'information_row'

const EditBar = withCitrusTheme(Box, (theme) => ({
  borderBottom: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.15)',
  cursor: 'pointer',
  height: '44px',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    height: '48px',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '-2px',
  },
}))

const EditBarContent = withCitrusTheme(Box, (theme) => ({
  margin: '0 auto',
  padding: `0 ${theme.spacing(3)}px`,
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    paddingLeft: '38px',
    paddingRight: '46px',
  },
}))

const BoxDetail = ({ iconName, text }: { iconName: IconNames; text: string }) => (
  <Box display={Display.Flex} alignItems={AlignItems.Center} gap={2}>
    <Icon size={[5, null, 6]} name={iconName} />
    <Text color={Color.ColdGrey_800} size={[2, null, 3]}>
      {text}
    </Text>
  </Box>
)

const getLabel = (boxTitle: string, deliveryDate: string, basketRecipesCount: number) => {
  const boxSizeAndDate = [boxTitle, deliveryDate].filter(Boolean).join(', ')
  const basket =
    basketRecipesCount > 0
      ? `, ${basketRecipesCount} ${basketRecipesCount === 1 ? 'recipe' : 'recipes'} in basket`
      : ''

  return `Edit box size or delivery date: ${boxSizeAndDate}${basket}`
}

type MenuExperienceProspectEditBarProps = {
  hasTopBorder?: boolean
}

export const MenuExperienceProspectEditBar = ({
  hasTopBorder = false,
}: MenuExperienceProspectEditBarProps) => {
  const dispatch = useDispatch()
  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const numPortions = useSelector(getNumPortions)
  const basketDate = useSelector(getBasketDate)
  const basketRecipesCount = useSelector(getBasketRecipesCount)

  if (!hasProspectNotSeenWizard) {
    return null
  }

  const boxTitle = boxTypes[numPortions]?.title ?? `${numPortions} person box`
  const deliveryDate = basketDate ? moment(basketDate).format('ddd D MMM') : ''

  const openBoxSummary = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    const componentDesign =
      event.target instanceof Element &&
      event.target.closest('[data-testid="prospect-edit-bar-cart"]')
        ? 'shopping_basket_icon'
        : 'information_banner'

    registerBoxSummaryTreatmentFocusTarget(event.currentTarget)
    dispatch(boxSummaryVisibilityChange(true))
    dispatch(
      trackEditBoxSizeOrDate(boxTitle, {
        section: trackingSection,
        component_design: componentDesign,
      }),
    )
  }

  return (
    <EditBar
      data-testid="prospect-edit-bar"
      role="button"
      tabIndex={0}
      aria-label={getLabel(boxTitle, deliveryDate, basketRecipesCount)}
      bg={Color.White}
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_100}
      borderTopWidth={hasTopBorder ? 0.5 : 0}
      onClick={openBoxSummary}
      onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          openBoxSummary(event)
        }
      }}
    >
      <EditBarContent
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        alignItems={AlignItems.Center}
        maxWidth="1600px"
        height="100%"
      >
        <Box display={Display.Flex} alignItems={AlignItems.Center} gap={4}>
          <BoxDetail iconName="meals_per_box" text={boxTitle} />
          {deliveryDate && <BoxDetail iconName="delivery" text={deliveryDate} />}
        </Box>
        <Box data-testid="prospect-edit-bar-cart" position={DisplayPosition.Relative}>
          <Icon size={6} variant={IconVariant.Actionable} name="shopping_basket" />
          {basketRecipesCount > 0 && (
            <Box
              aria-hidden="true"
              data-testid="prospect-edit-bar-basket-count"
              position={DisplayPosition.Absolute}
              top="-6px"
              right="-6px"
              bg={Color.Secondary_600}
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
              height="18px"
              width="18px"
              paddingH={1}
              borderRadius="round"
            >
              <Text color={Color.White} fontFamily={FontFamily.Bold} size={0}>
                {basketRecipesCount}
              </Text>
            </Box>
          )}
        </Box>
      </EditBarContent>
    </EditBar>
  )
}
